import React, { useEffect, useMemo, useRef } from "react";
import { CircleMarker, MapContainer as LeafletMap, Marker, TileLayer, Tooltip, useMap, useMapEvents } from "react-leaflet";
import L from "leaflet";
import MarkerClusterGroup from "react-leaflet-cluster";
import spotGreenIcon from "../../assets/icons/map-icon.svg";
import spotSelectedIcon from "../../assets/icons/map-icon-selected.svg";
import MapZoomLocateControl from "./MapZoomLocateControl";
import "./MapContainer.css";

const DEFAULT_CENTER = [53.885, 27.554];
const DEFAULT_ZOOM = 7;
const MAP_VIEW_STORAGE_KEY = "mapPage:view:v1";
const DISABLE_CLUSTERING_AT_ZOOM = 13;

const SPOT_ICON_SIZE = [60, 40];
const SPOT_ICON_ANCHOR = [30, 40];

const spotIcon = L.icon({ iconUrl: spotGreenIcon, iconSize: SPOT_ICON_SIZE, iconAnchor: SPOT_ICON_ANCHOR });
const spotIconSelected = L.icon({ iconUrl: spotSelectedIcon, iconSize: SPOT_ICON_SIZE, iconAnchor: SPOT_ICON_ANCHOR });
const clusterIconFactory = (cluster) => {
    const count = cluster.getChildCount();
    return L.divIcon({
        html: `<span>${count}</span>`,
        className: "map-cluster-badge",
        iconSize: [38, 38],
    });
};

function clamp(value, min, max) {
    return Math.min(Math.max(value, min), max);
}

function interpolateColor(start, end, t) {
    const ratio = clamp(t, 0, 1);
    const r = Math.round(start.r + (end.r - start.r) * ratio);
    const g = Math.round(start.g + (end.g - start.g) * ratio);
    const b = Math.round(start.b + (end.b - start.b) * ratio);
    return `rgb(${r}, ${g}, ${b})`;
}

function getDepthColor(depth) {
    const thresholdDepth = 7;
    const white = { r: 255, g: 255, b: 255 };
    const blue = { r: 10, g: 47, b: 112 };
    const red = { r: 220, g: 38, b: 38 };

    if (!Number.isFinite(depth)) return "rgb(255, 255, 255)";
    if (depth <= thresholdDepth) {
        const t = depth <= 0 ? 0 : depth / thresholdDepth;
        return interpolateColor(white, blue, t);
    }

    const t = (depth - thresholdDepth) / thresholdDepth;
    return interpolateColor(blue, red, t);
}

function readStoredMapView() {
    if (typeof window === "undefined") return null;
    try {
        const raw = window.localStorage.getItem(MAP_VIEW_STORAGE_KEY);
        if (!raw) return null;
        const parsed = JSON.parse(raw);
        const lat = Number(parsed?.lat);
        const lng = Number(parsed?.lng);
        const zoom = Number(parsed?.zoom);
        if (!Number.isFinite(lat) || !Number.isFinite(lng) || !Number.isFinite(zoom)) return null;
        return { center: [lat, lng], zoom };
    } catch {
        return null;
    }
}

function persistMapView(center, zoom) {
    if (typeof window === "undefined") return;
    const lat = Number(center?.lat);
    const lng = Number(center?.lng);
    const z = Number(zoom);
    if (!Number.isFinite(lat) || !Number.isFinite(lng) || !Number.isFinite(z)) return;
    try {
        window.localStorage.setItem(MAP_VIEW_STORAGE_KEY, JSON.stringify({ lat, lng, zoom: z }));
    } catch {
        // Ignore localStorage errors (e.g. private mode restrictions).
    }
}

function isMarkerClickTarget(target) {
    if (!target?.closest) return false;
    return Boolean(
        target.closest(".leaflet-marker-icon.leaflet-interactive")
        || target.closest(".map-cluster-badge")
        || target.closest(".leaflet-interactive")
    );
}

function refreshMarkerClusterLayers(map) {
    map.eachLayer((layer) => {
        if (typeof layer._moveEnd === "function") {
            layer._moveEnd();
        } else if (typeof layer.refreshClusters === "function") {
            layer.refreshClusters();
        }
    });
}

function ViewportListener({ onViewportChange, onMapClick }) {
    const map = useMap();

    const emitViewport = React.useCallback(() => {
        if (!onViewportChange) return;
        const bounds = map.getBounds();
        const center = map.getCenter();
        const zoom = map.getZoom();
        onViewportChange({
            bounds: {
                north: bounds.getNorth(),
                south: bounds.getSouth(),
                east: bounds.getEast(),
                west: bounds.getWest(),
            },
            center: {
                lat: center.lat,
                lng: center.lng,
            },
            zoom,
        });
    }, [map, onViewportChange]);

    React.useEffect(() => {
        map.whenReady(emitViewport);
    }, [map, emitViewport]);

    useMapEvents({
        click(event) {
            if (!onMapClick) return;
            if (isMarkerClickTarget(event.originalEvent?.target)) {
                return;
            }
            onMapClick();
        },
        moveend() {
            persistMapView(map.getCenter(), map.getZoom());
            emitViewport();
        },
    });

    return null;
}

function FlyToSelection({ selectedCoords }) {
    const map = useMap();
    React.useEffect(() => {
        if (!selectedCoords) return;
        map.flyTo(selectedCoords, Math.max(map.getZoom(), 12), { duration: 0.7 });
    }, [map, selectedCoords]);
    return null;
}

function ClusterLayerRefresh({ revision }) {
    const map = useMap();

    useEffect(() => {
        if (!revision) return undefined;

        let cancelled = false;

        const syncClusters = () => {
            if (cancelled) return;
            refreshMarkerClusterLayers(map);
        };

        const nudgeMap = () => {
            if (cancelled) return;
            map.panBy([1, 0], { animate: false });
            map.panBy([-1, 0], { animate: false });
            syncClusters();
        };

        syncClusters();
        const rafId = window.requestAnimationFrame(nudgeMap);
        const timeoutId = window.setTimeout(nudgeMap, 120);
        const lateTimeoutId = window.setTimeout(nudgeMap, 320);

        return () => {
            cancelled = true;
            window.cancelAnimationFrame(rafId);
            window.clearTimeout(timeoutId);
            window.clearTimeout(lateTimeoutId);
        };
    }, [map, revision]);

    return null;
}

const SpotMarker = React.memo(function SpotMarker({ spot, isSelected, onSpotClickRef }) {
    const lat = Number(spot.latitude);
    const lng = Number(spot.longitude);
    const icon = isSelected ? spotIconSelected : spotIcon;

    const eventHandlers = useMemo(() => ({
        click(event) {
            if (event?.originalEvent) {
                L.DomEvent.stopPropagation(event.originalEvent);
            }
            onSpotClickRef.current?.(spot);
        },
    }), [spot, onSpotClickRef]);

    if (!Number.isFinite(lat) || !Number.isFinite(lng)) {
        return null;
    }

    return (
        <Marker
            position={[lat, lng]}
            icon={icon}
            zIndexOffset={isSelected ? 1000 : 0}
            bubblingMouseEvents={false}
            eventHandlers={eventHandlers}
        />
    );
});

function MapContainer({
    spots,
    depthPoints = [],
    onSpotClick,
    onViewportChange,
    onMapClick,
    selectedCoords,
    selectedSpotId = null,
    markerSyncKey = 0,
}) {
    const storedView = React.useMemo(() => readStoredMapView(), []);
    const initialCenter = storedView?.center || DEFAULT_CENTER;
    const initialZoom = storedView?.zoom || DEFAULT_ZOOM;
    const normalizedSelectedSpotId = Number(selectedSpotId);
    const hasSelectedSpot = Number.isFinite(normalizedSelectedSpotId) && normalizedSelectedSpotId > 0;
    const clusterRefreshRevision = markerSyncKey || (hasSelectedSpot ? normalizedSelectedSpotId : 0);
    const onSpotClickRef = useRef(onSpotClick);

    useEffect(() => {
        onSpotClickRef.current = onSpotClick;
    }, [onSpotClick]);

    return (
        <LeafletMap center={initialCenter} zoom={initialZoom} zoomControl={false} style={{ height: "100%", width: "100%" }}>
            <TileLayer attribution="&copy; OpenStreetMap contributors" url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
            <MapZoomLocateControl />
            <ViewportListener onViewportChange={onViewportChange} onMapClick={onMapClick} />
            <FlyToSelection selectedCoords={selectedCoords} />
            <ClusterLayerRefresh revision={clusterRefreshRevision} />
            <MarkerClusterGroup
                iconCreateFunction={clusterIconFactory}
                disableClusteringAtZoom={DISABLE_CLUSTERING_AT_ZOOM}
                showCoverageOnHover={false}
                spiderfyOnMaxZoom={false}
                maxClusterRadius={60}
            >
                {spots.map((spot) => (
                    <SpotMarker
                        key={spot.id}
                        spot={spot}
                        isSelected={hasSelectedSpot && Number(spot.id) === normalizedSelectedSpotId}
                        onSpotClickRef={onSpotClickRef}
                    />
                ))}
            </MarkerClusterGroup>
            {depthPoints.map((point) => {
                const lat = Number(point?.lat);
                const lng = Number(point?.lng);
                const depth = Number(point?.depth);
                if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null;
                const isOverview = Boolean(point?.overview || point?.clustered);
                if (isOverview) {
                    return (
                        <CircleMarker
                            key={`depth-overview-${point.id ?? `${lat}-${lng}`}`}
                            center={[lat, lng]}
                            radius={5}
                            interactive={false}
                            bubblingMouseEvents={false}
                            pathOptions={{ color: "#9ca3af", fillColor: "#9ca3af", fillOpacity: 0.35, weight: 1 }}
                        />
                    );
                }
                const fillColor = getDepthColor(depth);
                return (
                    <CircleMarker
                        key={`depth-${point.id ?? `${lat}-${lng}`}`}
                        center={[lat, lng]}
                        radius={4}
                        interactive={false}
                        bubblingMouseEvents={false}
                        pathOptions={{ color: fillColor, fillColor, fillOpacity: 0.95, weight: 1 }}
                    >
                        <Tooltip direction="top" offset={[0, -6]} interactive={false}>
                            {`Глубина: ${Number.isFinite(depth) ? depth : "-"} м`}
                        </Tooltip>
                    </CircleMarker>
                );
            })}
        </LeafletMap>
    );
}

const MemoizedMapContainer = React.memo(MapContainer);

export default MemoizedMapContainer;
